import axios from "axios";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { computeDestinationPoint } from "geolib";

const accessToken = process.env.MAPBOX_ACCESS_TOKEN;
const mapStyle = `mapbox://styles/mapbox/dark-v9`;

// Coordinates for the center of the U.S.
const usaLat = 38.7638871;
const usaLng = -97.5481278;

// Returns an offset value of the coordinates in {latitude: decimal , longitude: decimal } format
function offsetCoordinate(coordinates, minDistance, maxDistance) {
  const randomIntInclusive = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };
  // We want a random bearing between 0 - 360 deg
  // Bearing = the direction we are moving from north
  let bearing = randomIntInclusive(0, 360);

  // We want a range from 0 to maxDistance in meters
  let distance = randomIntInclusive(minDistance, maxDistance);

  // create a new coordinate
  return computeDestinationPoint(coordinates, distance, bearing);
}

function haveSameCoordinates(coordinateA, coordinateB) {
  return (
    coordinateA.geometry.coordinates[0] ===
      coordinateB.geometry.coordinates[0] &&
    coordinateA.geometry.coordinates[1] === coordinateB.geometry.coordinates[1]
  );
}

class ElectionMap {
  constructor() {
    mapboxgl.accessToken = accessToken;

    // FIXME: Write an edge case for when WebGL isn't supported
    this.map = new mapboxgl.Map({
      container: "ElectionMap", // container id of the div storing the map
      style: mapStyle, // the style that is being used
      center: [usaLng, usaLat], // Points to the center of the USA
      zoom: 3.2, // zoom level
      scrollZoom: false,
    });

    this.map.on("load", () => {
      axios
        .get("/api/election-map")
        .then((response) => {
          // Converts the response upcoming election date field to pretty date format: November 3, 2020
          let { features } = response.data;

          // Change coordinates that have the same values
          for (let i = 0; i < features.length; i += 1) {
            let feature = features[i];
            for (let j = i + 1; j < features.length; j += 1) {
              if (haveSameCoordinates(feature, features[j])) {
                let { latitude, longitude } = offsetCoordinate(
                  feature.geometry.coordinates,
                  3200,
                  8000
                );

                feature.geometry.coordinates = [longitude, latitude];
              }
            }
          }

          // Convert date to a print friendly format
          response.data.features.forEach((feature) => {
            if (feature.properties.upcomingElectionDate) {
              // Convert the date into an array storing each part of date in a separate index
              // [Year, Month, Day]
              let yearMonthDay = feature.properties.upcomingElectionDate.split(
                "-"
              );

              // Subtracting the month by one because the first month start at 0
              let date = new Date(
                yearMonthDay[0],
                yearMonthDay[1] - 1,
                yearMonthDay[2]
              );

              // leveraging the system's locale date to create a date format that looks like November 3, 2020
              feature.properties.upcomingElectionDate = date.toLocaleString(
                "default",
                {
                  month: "long",
                  year: "numeric",
                  day: "numeric",
                }
              );
            }
          });

          this.init(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  }

  init(geojson) {
    // create custom markers & add it to the map
    // https://docs.mapbox.com/mapbox-gl-js/example/custom-marker-icons/
    geojson.features.forEach((feature) => {
      let marker = this.createMarker();

      marker
        .setLngLat(feature.geometry.coordinates)
        .setPopup(this.createPopup(feature))
        .addTo(this.map);

      // Changes marker state when clicked
      marker.getElement().addEventListener("click", (event) => {
        // Prevents the click event to bubble to the popup so that we can handle the toggling process.
        // This prevents the animation from going out of sync w/ the popup displaying
        // https://github.com/mapbox/mapbox-gl-js/issues/5289
        event.stopPropagation();

        // retrieves current open marker and close it
        let markerEl = event.target;

        // Accounts for the possibility of clicking on a marker's children. If that happens,
        // we still want to toggle .is-open on the .ElectionMarker -- not their children.
        if (!markerEl.classList.contains("ElectionMarker")) {
          markerEl = markerEl.closest(".ElectionMarker");
        }

        if (markerEl) {
          markerEl.classList.toggle("is-open");
          marker.togglePopup();
        }
      });
    });

    // Create navigational controls for the map
    let controls = new mapboxgl.NavigationControl();
    this.map.addControl(controls, "bottom-right");
  }

  createMarker() {
    let { Marker } = mapboxgl;
    let el = document.createElement("div");
    let icon = document.createElement("div");

    el.classList.add("ElectionMarker");
    icon.classList.add("ElectionMarker-icon");

    el.appendChild(icon);

    return new Marker(el);
  }

  createPopup(feature) {
    const { Popup } = mapboxgl;
    let markerSize = 46;
    let popupWidth = 350;

    // FIXME: Create variables for each repetitive math formula
    // We are using offset values to dynamically position the popup right outside of the marker
    const offset = {
      bottom: [-(popupWidth - markerSize) / 2, -markerSize / 2],
      top: [(popupWidth - markerSize) / 2, markerSize / 2],
      "top-right": [-markerSize / 2, -markerSize / 2],
      "top-left": [markerSize / 2, -markerSize / 2],
      "bottom-right": [-markerSize / 2, markerSize / 2],
      "bottom-left": [markerSize / 2, markerSize / 2],
    };

    return new Popup({
      // removes close button from the top right corner
      closeButton: false,
      // Offset applied to the popup's location
      offset: offset,
      // prevents the popup from closing when the map is clicked
      closeOnClick: false,
    }).setHTML(this.createPopupHTML(feature.properties));
  }

  createPopupHTML(properties) {
    const { person, districtName, upcomingElectionDate } = properties;

    return `
      <div class="ElectionPopup">
        <h3 class="ElectionPopup-header">Prosecutor</h3>
        <p class="ElectionPopup-place">${person.firstName} ${
      person.lastName
    }</p>

        <h4 class="ElectionPopup-header">District:</h4>
        <p class="ElectionPopup-text">${districtName}</p>

        <h4 class="ElectionPopup-header">In office Since:</h4>
        <p class="ElectionPopup-text">${
          person.yearElected ? person.yearElected : "N/A"
        }</p>

        ${
          upcomingElectionDate
            ? `
              <h4 class="ElectionPopup-header">Next Election:</h4>
              <p class="ElectionPopup-text">${upcomingElectionDate}</p>
        `
            : ""
        }

        <p class="ElectionPopup-text"><a href="${
          person.profilePage
        }">Learn more about ${person.firstName} ${person.lastName}</a></p>
      </div>
    `;
  }
}

// Only instantiate the map if the component is on the page
const mapEl = document.querySelector("#ElectionMap");
if (mapEl) {
  new ElectionMap();
}



// WEBPACK FOOTER //
// ./app/javascript/modules/election-map.js